import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);
export const supabaseConfigErrorMessage =
  'Missing Supabase env vars. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then restart the dev server.';

/** Fixed key so we can clear auth when switching persistence mode. */
const AUTH_STORAGE_KEY = 'sh-supabase-auth';

/**
 * Remembers which browser storage holds the Supabase session (survives full page reload).
 * Without this, `persistenceTarget` always reset to `local` on reload and session-only logins
 * were never read back from sessionStorage.
 */
const PERSISTENCE_PREF_KEY = 'sh-auth-persistence-target';

/**
 * Where to persist the Supabase session.
 * - localStorage: survives browser restarts ("keep me signed in")
 * - sessionStorage: cleared when the tab/window session ends
 */
let persistenceTarget =
  typeof window !== 'undefined'
    ? (() => {
        try {
          const v = window.localStorage.getItem(PERSISTENCE_PREF_KEY);
          return v === 'session' ? 'session' : 'local';
        } catch (_e) {
          return 'local';
        }
      })()
    : 'local';

function getStore() {
  if (typeof window === 'undefined') return null;
  return persistenceTarget === 'session' ? window.sessionStorage : window.localStorage;
}

const hybridStorage = {
  getItem: (key) => {
    const store = getStore();
    return store ? store.getItem(key) : null;
  },
  setItem: (key, value) => {
    const store = getStore();
    if (store) store.setItem(key, value);
  },
  removeItem: (key) => {
    const store = getStore();
    if (store) store.removeItem(key);
  },
};

/**
 * Call before sign-in so the new session is written to the right storage.
 * Clears the shared auth key from both storages to avoid stale tokens.
 */
export function configureAuthPersistence(keepSignedIn) {
  persistenceTarget = keepSignedIn ? 'local' : 'session';
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PERSISTENCE_PREF_KEY, persistenceTarget);
    } catch (_e) {
      // ignore
    }
  }
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (_e) {
    // ignore
  }
}

const resolvedSupabaseUrl = supabaseUrl || 'http://localhost:54321';
const resolvedSupabaseAnonKey = supabaseAnonKey || 'missing-anon-key';

if (!isSupabaseConfigured) {
  console.error(supabaseConfigErrorMessage);
}

export const supabase = createClient(resolvedSupabaseUrl, resolvedSupabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: hybridStorage,
    storageKey: AUTH_STORAGE_KEY,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});
